// POM-6199: whether a line item can be split by quantity. Selecting items on the order is
// the only way into Fill Later now — the 3-dot menu entry is gone — so this decides which
// rows get a checkbox and, through that, what the Fill Later button can act on.
import lineItemStatus from 'enums/lineItem.enum';
import orderStatus from 'enums/order.enum';

/**
 * POM-6148: eligible only while the item sits in product fulfillment. PRODUCT_FULFILLED and
 * anything downstream of it is locked — the quantity has already shipped or is about to. A
 * single-item order is allowed; the Cannot Fill guard is not copied here.
 */
export const canSplitLineItemByQuantity = ({
  itemStatus,
  processingState,
  isProcessedExternal,
  currentOrderStatus,
  hasSplitAccess,
  taggedForReview,
}) =>
  itemStatus === lineItemStatus.PRODUCT_FULFILLMENT &&
  processingState === 'PROCESSING' &&
  !isProcessedExternal &&
  !!hasSplitAccess &&
  !taggedForReview &&
  currentOrderStatus !== orderStatus.EXPIRED;

/**
 * An item under review is not settled enough to defer: its quantity, or whether it is dispensed
 * at all, can still change. It was already unsplittable, because the split reuses the print
 * checkbox and that is closed while an item is in review - but the two whole-item moves did not
 * ask, so the same order refused one route to Fill Later and offered two others. Review now
 * blocks every route, and the menu hides what it cannot offer instead of greying it.
 */
export const isLineItemInReview = (item) => !!item?.taggedForReview;

/**
 * Processing states an item has already moved past: it has shipped, is shipping, has been
 * transferred out, cancelled, or is parked already. None of them can be deferred.
 */
const MOVE_BLOCKING_PROCESSING_STATES = [
  'SHIPPED',
  'IN_SHIPMENT',
  'READY_FOR_SHIPMENT',
  'TRANSFERRED',
  'CANCELED',
  'FILL_LATER',
];

/** Line items already closed out; they neither move nor block the move. Mirrors the server. */
const TERMINAL_LINE_ITEM_STATUSES = [lineItemStatus.CANCELED, lineItemStatus.SPLIT];

/** An order in one of these has left fulfilment for good, or is already parked: no button at all. */
const HIDDEN_ORDER_STATUSES = [
  orderStatus.SHIPPED,
  orderStatus.CANCELED,
  orderStatus.EXPIRED,
  orderStatus.TRANSFERRED,
  orderStatus.FILL_LATER,
];

/**
 * POM-6180: whether the WHOLE order can move to Fill Later. Mirrors moveOrderToFillLater's gates
 * so the button and the server never disagree: the order must be OPEN and every live line item
 * must be in product fulfillment and processing. A single ineligible item refuses the order.
 *
 * Returns `hidden` when the action should not appear at all (no permission, terminal or parked
 * order), otherwise `eligible` plus a `reason` worded for the disabled button's tooltip. The
 * live line items come back too, so the dialog lists exactly what the server will move.
 */
export const canMoveOrderToFillLater = ({ order, hasMoveAccess }) => {
  const lineItems = (order?.lineItems ?? []).filter(
    (item) => !TERMINAL_LINE_ITEM_STATUSES.includes(item.lineItemStatus)
  );

  if (!hasMoveAccess || HIDDEN_ORDER_STATUSES.includes(order?.status)) {
    return { hidden: true, eligible: false, lineItems };
  }

  // Greyed with a reason, not hidden, and read before every other refusal so that the review is
  // what gets named. A choice that disappears leaves the operator guessing whether the order is
  // wrong or the screen is, and the count says how much is holding it up.
  const itemsInReview = lineItems.filter(isLineItemInReview).length;

  if (itemsInReview) {
    const subject = itemsInReview === 1 ? 'line item is' : 'line items are';

    return {
      eligible: false,
      reason: `${itemsInReview} ${subject} in review, so the whole order cannot be moved to Fill Later.`,
      lineItems,
    };
  }

  if (order?.isProcessedExternal) {
    return {
      eligible: false,
      reason: 'Externally processed orders cannot be moved to Fill Later.',
      lineItems,
    };
  }

  if (order?.status !== orderStatus.OPEN) {
    return { eligible: false, reason: 'Only an open order can be moved to Fill Later.', lineItems };
  }

  if (!lineItems.length) {
    return { eligible: false, reason: 'This order has no line items to move.', lineItems };
  }

  const everyItemEligible = lineItems.every(
    (item) =>
      item.lineItemStatus === lineItemStatus.PRODUCT_FULFILLMENT &&
      item.processingState === 'PROCESSING'
  );

  if (!everyItemEligible) {
    return {
      eligible: false,
      reason: 'Every line item must be in product fulfillment and processing to move the order.',
      lineItems,
    };
  }

  return { eligible: true, lineItems };
};
/**
 * POM-6320: whether a line item can be moved to Fill Later at all. Already filled, shipped or
 * cancelled items cannot be deferred; nor can one under review. The API enforces this again under
 * a row lock, so this only decides what is offered.
 *
 * Lives here rather than in the screen that renders the button. Order View and the patient profile
 * both offer the same three Fill Later scopes, and a fulfilment gate stated twice is a gate that
 * can be changed once — the two screens would then disagree about the same order without anything
 * failing to say so.
 */
export const isMovableLineItem = (item) =>
  !MOVE_BLOCKING_PROCESSING_STATES.includes(item?.processingState) &&
  item?.lineItemStatus !== 'PRODUCT_FULFILLED' &&
  !isLineItemInReview(item);

/**
 * Whether the SELECTED-ITEMS scope applies: a partial move needs the permission, something movable
 * to move, and more than one item — on a single-item order, moving the only item is a Cancel, not
 * a partial fill later.
 */
export const canMoveSelectedItemsToFillLater = ({ lineItems = [], hasSplitAccess }) =>
  !!hasSplitAccess && lineItems.length > 1 && lineItems.some(isMovableLineItem);

/**
 * The split-by-quantity scope acts on whatever is ticked for printing — Fill Later reuses those
 * checkboxes rather than adding a second column of its own. Only the ticked items that can
 * actually be split reach the dialog; the rest are counted, so the caller can name them instead of
 * dropping them silently.
 */
export const selectSplittableLineItems = ({
  lineItems = [],
  selectedLineItemIds = [],
  isProcessedExternal,
  currentOrderStatus,
  hasSplitAccess,
}) => {
  const isSelected = (item) => selectedLineItemIds.includes(item.lineItemId);

  const selected = lineItems.filter(
    (item) =>
      isSelected(item) &&
      canSplitLineItemByQuantity({
        itemStatus: item.lineItemStatus,
        processingState: item.processingState,
        isProcessedExternal,
        currentOrderStatus,
        hasSplitAccess,
        taggedForReview: item.taggedForReview,
      })
  );

  return {
    selected,
    skippedCount: lineItems.filter(isSelected).length - selected.length,
  };
};

export default canSplitLineItemByQuantity;
